"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const UR_AREA_LINKS = [
  { href: "/ur/area/hokkaitohoku/hokkaido", label: "北海道" },
  { href: "/ur/area/kansai/osaka", label: "大阪府" },
  { href: "/ur/area/hokkaitohoku/miyagi", label: "宮城県" },
  { href: "/ur/area/kansai/hyogo", label: "兵庫県" },
  { href: "/ur/area/kanto/tokyo", label: "東京都" },
  { href: "/ur/area/kansai/kyoto", label: "京都府" },
  { href: "/ur/area/kanto/kanagawa", label: "神奈川県" },
  { href: "/ur/area/kansai/shiga", label: "滋賀県" },
  { href: "/ur/area/kanto/chiba", label: "千葉県" },
  { href: "/ur/area/kansai/nara", label: "奈良県" },
  { href: "/ur/area/kanto/saitama", label: "埼玉県" },
  { href: "/ur/area/kansai/wakayama", label: "和歌山県" },
  { href: "/ur/area/kanto/ibaraki", label: "茨城県" },
  { href: "/ur/area/chugoku/okayama", label: "岡山県" },
  { href: "/ur/area/tokai/aichi", label: "愛知県" },
  { href: "/ur/area/chugoku/hiroshima", label: "広島県" },
  { href: "/ur/area/tokai/mie", label: "三重県" },
  { href: "/ur/area/chugoku/yamaguchi", label: "山口県" },
  { href: "/ur/area/tokai/gifu", label: "岐阜県" },
  { href: "/ur/area/kyushu/fukuoka", label: "福岡県" },
];

const JKK_AREA_LINKS = [
  { href: "/jkk/area/setagaya", label: "世田谷区" },
  { href: "/jkk/area/nerima", label: "練馬区" },
  { href: "/jkk/area/koto", label: "江東区" },
  { href: "/jkk/area/adachi", label: "足立区" },
  { href: "/jkk/area/itabashi", label: "板橋区" },
  { href: "/jkk/area/katsushika", label: "葛飾区" },
  { href: "/jkk/area/edogawa", label: "江戸川区" },
  { href: "/jkk/area/kita", label: "北区" },
  { href: "/jkk/area/hachioji", label: "八王子市" },
  { href: "/jkk/area/tama", label: "多摩市" },
  { href: "/jkk/area/machida", label: "町田市" },
  { href: "/jkk/area/hino", label: "日野市" },
];

/** redirectParam は認証画面の ?redirect=（(auth)/layout.tsx から渡す） */
export function Footer({ redirectParam = "" }: { redirectParam?: string }) {
  const pathname = usePathname();

  const [storedProvider, setStoredProvider] = useState<string | null>(null);
  useEffect(() => {
    setStoredProvider(localStorage.getItem("provider"));
  }, []);

  const isJkk = pathname.startsWith("/jkk") || redirectParam.startsWith("/jkk") || (!pathname.startsWith("/jkk") && !pathname.startsWith("/ur") && storedProvider === "jkk");
  const isBlog = pathname.startsWith("/blog") || pathname.startsWith("/guide") || pathname === "/";

  const areaLinks = isJkk ? JKK_AREA_LINKS : UR_AREA_LINKS;
  const prefix = isJkk ? "/jkk" : "/ur";

  return (
    <footer className="bg-navy-dark text-gray-400 mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
          <div className="col-span-2 md:col-span-2">
            <img
              src={isBlog ? "/logo_kuushitsu.svg" : isJkk ? "/logo_jkk.svg" : "/logo_ur.svg"}
              alt="空室情報.jp"
              className="h-7 mb-3"
            />
            <p className="text-sm leading-relaxed mb-3">
              {isBlog
                ? "公的賃貸住宅の空室情報をリアルタイムで収集・分析する情報サイトです。"
                : isJkk
                  ? "JKK東京（東京都住宅供給公社）の空室情報をリアルタイムで収集・分析する情報サイトです。"
                  : "UR賃貸住宅の空室情報をリアルタイムで収集・分析する情報サイトです。"}
            </p>
            <a
              href="https://x.com/ur_kuushitsu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-gray-400 hover:text-white transition-colors"
              aria-label="X (Twitter)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">ページ</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/ur" className="hover:text-white transition-colors">
                  UR空室情報
                </Link>
              </li>
              <li>
                <Link href="/jkk" className="hover:text-white transition-colors">
                  JKK空室情報
                </Link>
              </li>
              {!isBlog && (
                <>
                  <li>
                    <Link href={`${prefix}/vacancy`} className="hover:text-white transition-colors">
                      {isJkk ? "JKK空室情報に関して" : "UR空室情報に関して"}
                    </Link>
                  </li>
                  <li>
                    <Link href={`${prefix}/recent`} className="hover:text-white transition-colors">
                      最近の更新
                    </Link>
                  </li>
                  <li>
                    <Link href={`${prefix}/vacancy/count`} className="hover:text-white transition-colors">
                      空室一覧
                    </Link>
                  </li>
                </>
              )}
              <li>
                <Link href="/guide/ur" className="hover:text-white transition-colors">
                  UR引越しガイド
                </Link>
              </li>
              <li>
                <Link href="/guide/jkk" className="hover:text-white transition-colors">
                  JKK引越しガイド
                </Link>
              </li>
              <li>
                <Link href="/blog" className="hover:text-white transition-colors">
                  空室ジャーナル
                </Link>
              </li>
              <li>
                <Link href="/ur/terms" className="hover:text-white transition-colors">
                  利用規約
                </Link>
              </li>
              <li>
                <Link href="/ur/privacy" className="hover:text-white transition-colors">
                  プライバシーポリシー
                </Link>
              </li>
              <li>
                <Link href="/contact" className="hover:text-white transition-colors">
                  お問い合わせ
                </Link>
              </li>
            </ul>
          </div>
          {!isBlog && (
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">分析</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link href={`${prefix}/analytics`} className="hover:text-white transition-colors">
                    分析ツール一覧
                  </Link>
                </li>
                <li>
                  <Link href={`${prefix}/analytics/fill-speed`} className="hover:text-white transition-colors">
                    埋まるスピード
                  </Link>
                </li>
                <li>
                  <Link href={`${prefix}/analytics/hourly`} className="hover:text-white transition-colors">
                    時間帯別の傾向
                  </Link>
                </li>
                <li>
                  <Link href={`${prefix}/analytics/weekly`} className="hover:text-white transition-colors">
                    曜日別の動き
                  </Link>
                </li>
              </ul>
            </div>
          )}
          {!isBlog && (
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-white font-semibold text-sm mb-3">エリア</h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {areaLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-700 pt-8">
          <p className="text-xs leading-relaxed mb-4">
            {isBlog ? (
              <>
                本サイトは、UR都市機構およびJKK東京（東京都住宅供給公社）と一切関係がありません。
                公開されている情報を元に空室情報を独自に収集・分析しています。
                本サイトの内容は作成基準時点のものであり、予告無く変更または廃止される場合があります。
                最新の募集条件は必ず
                <a
                  href="https://www.ur-net.go.jp/chintai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  UR公式サイト
                </a>
                または
                <a
                  href="https://www.to-kousya.or.jp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  JKK東京公式サイト
                </a>
                をご確認ください。
              </>
            ) : isJkk ? (
              <>
                本サイトは、JKK東京（東京都住宅供給公社）と一切関係がありません。
                各物件の情報についてはJKK東京をはじめとして、インターネットや書籍等により
                公開されている情報を元に取りまとめています。本サイトの内容は作成基準時点のもの
                であり、予告無く変更または廃止される場合があります。また、このサイトに掲載されて
                いる情報の作成には万全を期していますが、当該情報の完全性を保証するものではありません。
                最新の募集条件は必ず
                <a
                  href="https://www.to-kousya.or.jp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  JKK東京公式サイト
                </a>
                をご確認ください。
              </>
            ) : (
              <>
                本サイトは、UR都市機構（独立行政法人都市再生機構）と一切関係がありません。
                各物件の情報についてはUR都市機構をはじめとして、インターネットや書籍等により
                公開されている情報を元に取りまとめています。本サイトの内容は作成基準時点のもの
                であり、予告無く変更または廃止される場合があります。また、このサイトに掲載されて
                いる情報の作成には万全を期していますが、当該情報の完全性を保証するものではありません。
                最新の募集条件は必ず
                <a
                  href="https://www.ur-net.go.jp/chintai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  UR公式サイト
                </a>
                をご確認ください。
              </>
            )}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
            <Link href="/ur/terms" className="hover:text-gray-300 transition-colors">
              利用規約
            </Link>
            <Link href="/ur/privacy" className="hover:text-gray-300 transition-colors">
              プライバシーポリシー
            </Link>
            <Link href="/contact" className="hover:text-gray-300 transition-colors">
              お問い合わせ
            </Link>
          </div>
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} 空室情報.jp All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
